import { Box, Slider, Stack, Typography } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { getSliderRange } from "@/helpers/indicativeMarketValuation";

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createStyles = (theme: Theme) => ({
  section: {
    width: "100%",
  },
  wrapper: {
    width: "100%",
    display: "grid",
    gap: "16px",
  },
  sliderTrack: {
    position: "relative",
    px: "16px",
  },
  activeValue: {
    // minWidth: "85px",
    // height: "45px",
    p: "10px 16px",
    m: "-12px 0 0 0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "4px",
    backgroundColor: "white",
    border: `1px solid ${theme.semanticColors.border.action}`,
    boxShadow: "0px 4px 2px rgba(0, 0, 0, 0.25)",
  },
  tickLabels: {
    position: "relative",
    display: "grid",
    gridTemplateColumns: "repeat(11, minmax(0, 1fr))",
    alignItems: "start",
    gap: "4px",
    width: "100%",
  },
  tickItem: {
    display: "grid",
    justifyItems: "center",
    alignItems: "start",
    minWidth: 0,
  },
  activeValueOverlay: {
    position: "absolute",
    top: 0,
    transform: "translateX(-50%)",
    pointerEvents: "none",
    zIndex: 1,
    pt: "12px",
  },
  tickContent: {
    display: "grid",
    justifyItems: "center",
    gap: "6px",
    minWidth: 0,
  },
  tickMark: {
    width: "1px",
    height: "8px",
    backgroundColor: theme.semanticColors.border.subtle,
  },
  tickLabel: {
    color: theme.semanticColors.text.primary,
    textAlign: "center",
    fontSize: "14px",
    lineHeight: "19px",
    whiteSpace: "nowrap",
  },
  slider: {
    px: 0,
    "& .MuiSlider-rail": {
      backgroundColor: theme.semanticColors.border.subtle,
      opacity: 1,
      height: "8px",
      borderRadius: "4px",
    },
    "& .MuiSlider-track": {
      backgroundColor: "transparent",
      border: "none",
      height: "8px",
      borderRadius: "4px",
    },
    "& .MuiSlider-thumb": {
      width: 24,
      height: 24,
      backgroundColor: "white",
      border: `2px solid ${theme.semanticColors.border.action}`,
      boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.22)",
      "&:hover, &.Mui-focusVisible, &.Mui-active": {
        boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.22)",
      },
      "&::before": {
        boxShadow: "none",
      },
    },
    "& .MuiSlider-mark": {
      display: "none",
    },
    "& .MuiSlider-markLabel": {
      display: "none",
    },
  },
});

interface FactorSliderProps {
  min: number;
  max: number;
  value: number;
  recommendedValue?: number | null;
  step?: number;
  roundValue?: boolean;
  title?: string;
  showTitle?: boolean;
  formatValue?: (value: number) => string;
  disabled?: boolean;
  onChange?: (_event: Event, value: number | number[]) => void;
}

export const FactorSlider = ({
  min,
  max,
  value,
  recommendedValue,
  step = 0.001,
  roundValue = false,
  title,
  showTitle = true,
  formatValue,
  disabled = false,
  onChange,
}: FactorSliderProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation("IndicativeMarketValuationPanel");
  const styles = createStyles(theme);
  const {
    min: normalizedMin,
    max: normalizedMax,
    value: normalizedValue,
  } = getSliderRange(
    { value_min: min, value_max: max, value, recommended_value: recommendedValue },
    { minKey: "value_min", maxKey: "value_max" },
  );
  const tickStep = (normalizedMax - normalizedMin) / 10;
  const formatFactor = (factor: number): string => {
    if (formatValue) {
      return formatValue(factor);
    }

    return roundValue
      ? Math.round(factor).toLocaleString("de-DE")
      : factor.toLocaleString("de-DE", { maximumFractionDigits: 3 });
  };
  const tickValues = Array.from({ length: 11 }, (_, index) => normalizedMin + tickStep * index);
  const thumbValue = Math.min(normalizedMax, Math.max(normalizedMin, normalizedValue));
  const activeValueLeftPercent =
    normalizedMax > normalizedMin ? ((thumbValue - normalizedMin) / (normalizedMax - normalizedMin)) * 100 : 0;
  return (
    <Stack gap="30px" sx={styles.section}>
      {showTitle && <Typography variant="h4">{title ?? t("statisticalBaseValueAdjustment")}</Typography>}
      <Box sx={styles.wrapper}>
        <Box sx={styles.sliderTrack}>
          <Slider
            aria-label={title ?? t("statisticalBaseValueAdjustment")}
            min={normalizedMin}
            max={normalizedMax}
            step={step}
            value={thumbValue}
            onChange={onChange}
            disabled={disabled}
            sx={styles.slider}
          />
        </Box>
        <Box sx={styles.tickLabels}>
          <Box sx={{ ...styles.activeValueOverlay, left: `${activeValueLeftPercent}%` }}>
            <Box sx={styles.activeValue}>
              <Typography variant="body1" color="primary.main">
                {formatFactor(normalizedValue)}
              </Typography>
            </Box>
          </Box>
          {tickValues.map((tickValue, index) => (
            <Box key={`${tickValue}-${index}`} sx={styles.tickItem}>
              <Box sx={styles.tickContent}>
                <Box sx={styles.tickMark} />
                <Typography variant="body1" sx={styles.tickLabel}>
                  {formatFactor(tickValue)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Stack>
  );
};
